import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import useStore from '../../store/useStore';
import { formatDuration, formatSize } from './videoApi';

// Clips from useClipQueue: upload progress, retry on failure, discard.
export default function PendingClipsPanel({ clips, onRetry, onDiscard }) {
  const { language } = useStore();
  const en = language === 'en';
  const [confirmKey, setConfirmKey] = useState(null);

  if (!clips.length) return null;

  const statusLabel = (clip) => {
    if (clip.status === 'done') return en ? 'Uploaded' : 'Загружено';
    if (clip.status === 'uploading')
      return `${en ? 'Uploading' : 'Загрузка'} ${Math.round(clip.progress * 100)}%`;
    if (clip.status === 'error') return en ? 'Not uploaded' : 'Не загружено';
    return clip.profileId
      ? en
        ? 'Waiting'
        : 'В очереди'
      : en
        ? 'Waits for save'
        : 'Ждёт сохранения';
  };
  const statusColor = (clip) =>
    clip.status === 'done'
      ? 'success'
      : clip.status === 'error'
        ? 'error'
        : 'default';

  return (
    <Stack spacing={1}>
      {clips.map((clip, i) => (
        <Box
          key={clip.key}
          sx={{
            border: '1px solid',
            borderColor: clip.status === 'error' ? 'error.main' : 'divider',
            borderRadius: 2,
            p: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {clip.title || `${en ? 'Clip' : 'Клип'} ${i + 1}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {clip.durationSec
                  ? `${formatDuration(clip.durationSec)} · `
                  : ''}
                {formatSize(clip.size)}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={statusLabel(clip)}
              color={statusColor(clip)}
              variant={clip.status === 'done' ? 'filled' : 'outlined'}
            />
            {clip.status === 'error' && (
              <Tooltip title={en ? 'Retry upload' : 'Повторить загрузку'}>
                <IconButton
                  size="small"
                  onClick={() => onRetry(clip.key)}
                  aria-label={en ? 'Retry upload' : 'Повторить загрузку'}
                >
                  <ReplayIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {clip.status !== 'done' && clip.status !== 'uploading' && (
              <Tooltip title={en ? 'Delete clip' : 'Удалить клип'}>
                <IconButton
                  size="small"
                  onClick={() => setConfirmKey(clip.key)}
                  aria-label={en ? 'Delete clip' : 'Удалить клип'}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {clip.status === 'uploading' && (
            <LinearProgress
              variant="determinate"
              value={Math.round(clip.progress * 100)}
              sx={{ mt: 0.75, height: 5, borderRadius: 3 }}
            />
          )}
          {clip.status === 'error' && (
            <Alert severity="error" sx={{ mt: 0.75, py: 0 }}>
              {clip.error}
              {en
                ? '. The clip is kept on this device until it is uploaded.'
                : '. Клип остаётся на этом устройстве, пока не загрузится.'}
            </Alert>
          )}
          {confirmKey === clip.key && (
            <Alert
              severity="warning"
              sx={{ mt: 0.75, py: 0 }}
              action={
                <>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setConfirmKey(null)}
                  >
                    {en ? 'No' : 'Нет'}
                  </Button>
                  <Button
                    color="error"
                    size="small"
                    onClick={() => {
                      setConfirmKey(null);
                      onDiscard(clip.key);
                    }}
                  >
                    {en ? 'Delete' : 'Удалить'}
                  </Button>
                </>
              }
            >
              {en
                ? 'Delete this clip? It has not been uploaded.'
                : 'Удалить клип? Он не загружен.'}
            </Alert>
          )}
        </Box>
      ))}
    </Stack>
  );
}
